import { Observable, throwError } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, tap } from 'rxjs/operators';
import { BatchConnection, BatchResponseItem } from './batch-connection';
import { Cim, CimMultiple, CimResult, CimSingle } from './cim';
import { headerConstants } from './http-constants';
import { Net } from './net';
import { NodeCimOutput, NodeConnection, NodeRequest, NodeRequestOptions } from './node-connection';
import { PowerShellAlternate } from './powershell-alternate';

/**
 * The CIM Connection class.
 */
export class CimConnection {
    /**
     * Initializes a new instance of the CimService class.
     *
     * @param nodeConnection the NodeConnection class instance injected.
     * @param batchConnection the BatchConnection class instance injected.
     */
    constructor(private nodeConnection: NodeConnection, private batchConnection: BatchConnection) { }

    /**
     * CIM GET MultipleInstances
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public getInstanceMultiple(
            nodeName: string,
            namespace: string,
            className: string,
            options?: NodeRequestOptions): Observable<CimMultiple> {
        this.setPowerShellParameters(options, NodeCimOutput.Multiple);
        const observable = PowerShellAlternate.create<CimMultiple>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlMultipleInstances(namespace, className);
        return this.nodeConnection.get(nodeName, cimUrl, <NodeRequest>options)
            .pipe(catchError(error => this.handleJeaFallback<CimMultiple>(error, nodeName, options)));
    }

    /**
     * CIM GET SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public getInstanceSingle(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            options?: NodeRequestOptions): Observable<CimSingle> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties);
        const observable = PowerShellAlternate.create<CimSingle>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        return this.nodeConnection.get(nodeName, cimUrl, <NodeRequest>options)
            .pipe(catchError(error => this.handleJeaFallback<CimSingle>(error, nodeName, options)));
    }

    /**
     * CIM POST InstanceMethod
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public invokeMethodInstance(
            nodeName: string,
            namespace: string,
            className: string,
            methodName: string,
            keyProperties: any,
            data?: any,
            options?: NodeRequestOptions): Observable<CimResult> {
        this.setPowerShellParameters(options, NodeCimOutput.Result, keyProperties, data && data.parameters);
        const observable = PowerShellAlternate.create<CimResult>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlInstanceMethod(namespace, className, methodName, keyProperties);
        return this.nodeConnection.post(nodeName, cimUrl, data, <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimResult>(error, nodeName, options)));
    }

    /**
     * CIM POST StaticMethod
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public invokeMethodStatic(
            nodeName: string,
            namespace: string,
            className: string,
            methodName: string,
            data?: any,
            options?: NodeRequestOptions): Observable<CimResult> {
        this.setPowerShellParameters(options, NodeCimOutput.Result, data && data.parameters);
        const observable = PowerShellAlternate.create<CimResult>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlStaticMethod(namespace, className, methodName);
        return this.nodeConnection.post(nodeName, cimUrl, data, <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimResult>(error, nodeName, options)));
    }

    /**
     * CIM PUT SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public setInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            data: any,
            options?: NodeRequestOptions): Observable<CimSingle> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties, data && data.properties);
        const observable = PowerShellAlternate.create<CimSingle>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        return this.nodeConnection.put(nodeName, cimUrl, data, <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimSingle>(error, nodeName, options)));
    }

    /**
     * CIM PATCH SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public modifyInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            data: any,
            options?: NodeRequestOptions): Observable<CimSingle> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties, data && data.properties);
        const observable = PowerShellAlternate.create<CimSingle>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        return this.nodeConnection.patch(nodeName, cimUrl, data, <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimSingle>(error, nodeName, options)));
    }

    /**
     * CIM DELETE SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public deleteInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            options?: NodeRequestOptions): Observable<CimSingle> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties);
        const observable = PowerShellAlternate.create<CimSingle>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        return this.nodeConnection.delete(nodeName, cimUrl, null, <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimSingle>(error, nodeName, options)));
    }

    /**
     * CIM POST WqlQuery
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param query the WQL string.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public getInstanceQuery(
            nodeName: string,
            namespace: string,
            query: string,
            options?: NodeRequestOptions): Observable<CimMultiple> {
        this.setPowerShellParameters(options, NodeCimOutput.Query, options && options.powerShellParameters);
        const observable = PowerShellAlternate.create<CimMultiple>(this.nodeConnection, nodeName, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlWqlQuery(namespace);
        return this.nodeConnection.post(nodeName, cimUrl, JSON.stringify({ query: query }), <NodeRequest>options)
                .pipe(catchError(error => this.handleJeaFallback<CimMultiple>(error, nodeName, options)));
    }

    /**
     * Handle the fallback from CIM to powershell with JEA, if applicable
     * @param error The error to handle
     * @param nodeName The node name
     * @param options The request options
     */
    private handleJeaFallback<T>(error: AjaxError, nodeName: string, options: NodeRequestOptions): Observable<T> {
        const authError = Net.isUnauthorized(error);
        const responseEndpoint = error && error.xhr && error.xhr.getResponseHeader(headerConstants.POWERSHELL_ENDPOINT);
        const requestEndpoint = options && options.powerShellEndpoint;

        if (authError && responseEndpoint && requestEndpoint !== responseEndpoint) {
            options.powerShellEndpoint = responseEndpoint;
            const powershellRequest = PowerShellAlternate.create<T>(this.nodeConnection, nodeName, options);
            if (powershellRequest) {
                return powershellRequest
                    .pipe(
                        tap(() => {
                            // The JEA request went through - persist this context in authorization manager.
                            this.nodeConnection.saveJeaContext(nodeName, responseEndpoint);
                        }));
            }
        }

        return throwError(() => error);
    }

    /**********************************
     * Cim Batch Section
     **********************************/

    /**
     * CIM GET MultipleInstances for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public getBatchInstanceMultiple(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Multiple);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);

        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlMultipleInstances(namespace, className);
        const urlList: string[] = [];
        for (let index = 0; index < nodeNamesList.length; index++) {
            urlList.push(cimUrl);
        }

        return this.batchConnection.get(nodeNamesList, urlList, <NodeRequest>options);
    }

    /**
     * CIM GET SingleInstance for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public getBatchInstanceSingle(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            keyProperties: any,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);

        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        const urlList: string[] = [];
        for (let index = 0; index < nodeNamesList.length; index++) {
            urlList.push(cimUrl);
        }

        return this.batchConnection.get(nodeNamesList, urlList, <NodeRequest>options);
    }

    /**
     * CIM POST InstanceMethod for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public invokeBatchMethodInstance(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            methodName: string,
            keyProperties: any,
            data?: any,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Result, keyProperties, data && data.parameters);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);

        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlInstanceMethod(namespace, className, methodName, keyProperties);
        const body = data ? JSON.stringify(data) : null;
        return this.cimBatchPost(nodeNamesList, cimUrl, body, options);
    }

    /**
     * CIM POST StaticMethod for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public invokeBatchMethodStatic(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            methodName: string,
            data?: any,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Result, data && data.parameters);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);

        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlStaticMethod(namespace, className, methodName);
        const body = data ? JSON.stringify(data) : null;
        return this.cimBatchPost(nodeNamesList, cimUrl, body, options);
    }

    /**
     * CIM PUT SingleInstance for list of nodes
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public setBatchInstance(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            keyProperties: any,
            data: any,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties, data && data.properties);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        const urlList: string[] = [];
        const dataList: string[] = [];
        for (let index = 0; index < nodeNamesList.length; index++) {
            urlList.push(cimUrl);
            dataList.push(JSON.stringify(data));
        }

        return this.batchConnection.put(nodeNamesList, urlList, dataList, <NodeRequest>options);
    }

    /**
     * CIM DELETE SingleInstance for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public deleteBatchInstance(
            nodeNamesList: string[],
            namespace: string,
            className: string,
            keyProperties: any,
            options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Single, keyProperties);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlSingleInstance(namespace, className, keyProperties);
        const urlList: string[] = [];
        for (let index = 0; index < nodeNamesList.length; index++) {
            urlList.push(cimUrl);
        }

        return this.batchConnection.delete(nodeNamesList, urlList, <NodeRequest>options);
    }

    /**
     * CIM POST WqlQuery for list of nodes
     *
     * @param nodeNamesList the Nodes to use for this request.
     * @param namespace the cim namespace.
     * @param query the WQL string.
     * @param options the options for this request.
     * @return Observable<BatchResponseItem[]> the query observable.
     */
    public getBatchInstanceQuery(nodeNamesList: string[], namespace: string, query: string, options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        this.setPowerShellParameters(options, NodeCimOutput.Query, options && options.powerShellParameters);
        const observable = PowerShellAlternate.createBatch(this.nodeConnection, this.batchConnection, nodeNamesList, options);
        if (observable) {
            return observable;
        }

        const cimUrl = Cim.cimUrlWqlQuery(namespace);
        const body = JSON.stringify({ query: query });
        return this.cimBatchPost(nodeNamesList, cimUrl, body, options);
    }

    /**
     * Cim batch post helper
     *
     * @param nodeNamesList The list of Nodes to run the call against
     * @param cimUrl The CIM end point to call
     * @param jsonBody The body of Post in json format.
     * @param options the request options.
     */
    private cimBatchPost(nodeNamesList: string[], cimUrl: string, jsonBody: string, options?: NodeRequestOptions): Observable<BatchResponseItem[]> {
        const urlList: string[] = [];
        const bodyList: string[] = [];
        for (let index = 0; index < nodeNamesList.length; index++) {
            urlList.push(cimUrl);
            if (jsonBody) {
                bodyList.push(jsonBody);
            }
        }

        return this.batchConnection.post(nodeNamesList, urlList, bodyList, <NodeRequest>options);
    }

    /**
     * Set PowerShell parameters to the options object.
     *
     * @param options The node request options.
     * @param outputType The output data type.
     * @param keys The key data.
     * @param data The arguments data.
     */
    private setPowerShellParameters(
            options: NodeRequestOptions,
            cimOutput: NodeCimOutput,
            keys?: any,
            data?: any): void {
        if (options == null || options.powerShell == null) {
            return;
        }

        options.powerShellContext = {
            cimOutput,
            parameters: { ...(keys || {}), ...(data || {}) }
        };
    }
}
